import {Button, FormControl, InputLabel, MenuItem, Select, Stack} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {type Dayjs} from "dayjs";
import {useTranslation} from "react-i18next";
import {type BillingGranularity, type LegalEntity} from "../AdminTypes";
import {SetStateAction, useCallback, useMemo, useState} from "react";
import {getInitialDateRange, toDayjs, toIsoDate} from "./billingUtils";

type BillingFiltersProps = {
    isLegalEntitiesLoading: boolean;
    legalEntities: LegalEntity[];
    isLoading: boolean;
    selectedLegalEntityId: string;
    selectedStartDate: string;
    selectedEndDate: string;
    selectedGranularity: string;
    onSelectedLegalEntityChange: (value: string) => void;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
    onGranularityChange: (value: SetStateAction<BillingGranularity>) => void;
};

const BillingFilters = ({
    isLegalEntitiesLoading,
    legalEntities,
    isLoading,
    selectedLegalEntityId,
    selectedStartDate,
    selectedEndDate,
    selectedGranularity,
    onSelectedLegalEntityChange,
    onStartDateChange,
    onEndDateChange,
    onGranularityChange,
}: BillingFiltersProps) => {
    const {t: translate} = useTranslation();

    const initialDateRange = useMemo(() => getInitialDateRange(), []);
    const [startDateDraft, setStartDateDraft] = useState<Dayjs | null>(() => toDayjs(initialDateRange.startDate));
    const [endDateDraft, setEndDateDraft] = useState<Dayjs | null>(() => toDayjs(initialDateRange.endDate));
    const [granularityDraft, setGranularityDraft] = useState<BillingGranularity>("MONTHLY");

    const hasSelectedLegalEntity: boolean = selectedLegalEntityId !== "";

    const isApplyDisabled: boolean =
        !hasSelectedLegalEntity ||
        isLoading ||
        (selectedStartDate === toIsoDate(startDateDraft) &&
            selectedEndDate === toIsoDate(endDateDraft) &&
            selectedGranularity === granularityDraft);

    const granularityOptions = useMemo<{label: string; value: BillingGranularity}[]>(
        () => [
            {label: translate("admin.billing.granularity.daily"), value: "DAILY"},
            {label: translate("admin.billing.granularity.weekly"), value: "WEEKLY"},
            {label: translate("admin.billing.granularity.monthly"), value: "MONTHLY"},
            {label: translate("admin.billing.granularity.yearly"), value: "YEARLY"},
        ],
        [translate],
    );

    const handleApplyFilters = useCallback(() => {
        const nextStartDate = toIsoDate(startDateDraft);
        const nextEndDate = toIsoDate(endDateDraft);
        onStartDateChange(nextStartDate);
        onEndDateChange(nextEndDate);
        onGranularityChange(granularityDraft);
    }, [endDateDraft, granularityDraft, onEndDateChange, onGranularityChange, onStartDateChange, startDateDraft]);

    return (
        <Stack
            direction={{xs: "column", xl: "row"}}
            spacing={2}
            justifyContent="space-between"
            alignItems={{xs: "stretch", xl: "flex-start"}}
        >
            <FormControl fullWidth sx={{minWidth: 280, maxWidth: 360}} disabled={isLegalEntitiesLoading}>
                <InputLabel id="billing-legal-entity-label">{translate("admin.shared.legalEntity")}</InputLabel>
                <Select
                    labelId="billing-legal-entity-label"
                    label={translate("admin.shared.legalEntity")}
                    value={selectedLegalEntityId}
                    onChange={(event) => onSelectedLegalEntityChange(event.target.value)}
                >
                    {legalEntities.map((legalEntity) => (
                        <MenuItem key={legalEntity.id} value={legalEntity.id}>
                            {legalEntity.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{flexGrow: 1}}>
                <DatePicker
                    label={translate("admin.shared.from")}
                    value={startDateDraft}
                    onChange={setStartDateDraft}
                    disabled={!hasSelectedLegalEntity}
                    sx={{minWidth: 220}}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                        },
                    }}
                />
                <DatePicker
                    label={translate("admin.shared.to")}
                    value={endDateDraft}
                    onChange={setEndDateDraft}
                    disabled={!hasSelectedLegalEntity}
                    sx={{minWidth: 220}}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                        },
                    }}
                />
                <FormControl fullWidth sx={{minWidth: 220}} disabled={!hasSelectedLegalEntity}>
                    <InputLabel id="billing-granularity-label">{translate("admin.billing.timeGranularity")}</InputLabel>
                    <Select
                        labelId="billing-granularity-label"
                        label={translate("admin.billing.timeGranularity")}
                        value={granularityDraft}
                        onChange={(event) => setGranularityDraft(event.target.value as BillingGranularity)}
                    >
                        {granularityOptions.map((granularityOption) => (
                            <MenuItem key={granularityOption.value} value={granularityOption.value}>
                                {granularityOption.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    disabled={isApplyDisabled}
                    sx={{minWidth: 160, alignSelf: {xs: "stretch", md: "center"}}}
                >
                    {translate("admin.billing.applyFilters")}
                </Button>
            </Stack>
        </Stack>
    );
};

export default BillingFilters;
